import { Group, Asset } from './AssetGroupsSlice';

export interface UpdateGroupParams {
  originalGroupName: string;
  newGroupName: string;
  newParentGroupName: string;
  childWells?: Asset[];
  isNewGroup?: boolean;
}

export interface GroupTreeOperationResult {
  updatedGroups: Group[] | null;
  updatedSelectedGroup?: Group | null;
}

/**
 * Helper function to find the current parent of a group
 */
export const getCurrentParentName = (groups: Group[] | null | undefined, groupName: string): string | null => {
  if (!groups) return null;

  for (const group of groups) {
    // Check if this group is a direct child
    if (group.childGroups.some((child) => child.assetGroupName === groupName)) {
      return group.assetGroupName;
    }

    // Recursively check children
    const parentInChild = getCurrentParentName(group.childGroups, groupName);
    if (parentInChild) {
      return parentInChild;
    }
  }

  // If not found in any group, it's at root level
  if (groups.some((group) => group.assetGroupName === groupName)) {
    return 'root';
  }

  return null;
};

/**
 * Helper function to recursively find and update a group in the tree
 */
const updateGroupInTree = (
  groups: Group[] | null | undefined,
  originalGroupName: string,
  newGroupName: string,
  childWells?: Asset[],
): Group[] | null => {
  if (!groups) return null;

  return groups.map((group: Group) => {
    // If this is the group we're updating
    if (group.assetGroupName === originalGroupName) {
      const updatedGroup: Group = {
        ...group,
        assetGroupName: newGroupName,
        assets: childWells || group.assets,
      };
      return updatedGroup;
    }

    // If this group has children, recursively update them
    const updatedChildGroups = updateGroupInTree(group.childGroups, originalGroupName, newGroupName, childWells);
    return {
      ...group,
      childGroups: updatedChildGroups || [],
    };
  });
};

/**
 * Helper function to remove a group from its current parent
 */
const removeGroupFromTree = (groups: Group[] | null | undefined, groupNameToRemove: string): Group[] | null => {
  if (!groups) return null;

  return groups
    .filter((group) => group.assetGroupName !== groupNameToRemove)
    .map((group) => {
      const updatedChildGroups = removeGroupFromTree(group.childGroups, groupNameToRemove);
      return {
        ...group,
        childGroups: updatedChildGroups || [],
      };
    });
};

/**
 * Helper function to add a group to its new parent
 */
const addGroupToParent = (
  groups: Group[] | null | undefined,
  groupToAdd: Group,
  newParentGroupName: string,
): Group[] | null => {
  if (!groups) return null;

  return groups.map((group: Group) => {
    if (group.assetGroupName === newParentGroupName) {
      return {
        ...group,
        childGroups: [...group.childGroups, groupToAdd],
      };
    }

    const updatedChildGroups = addGroupToParent(group.childGroups, groupToAdd, newParentGroupName);
    return {
      ...group,
      childGroups: updatedChildGroups || group.childGroups,
    };
  });
};

/**
 * Helper function to find a group in the tree structure
 */
const findGroupInTree = (groups: Group[] | null | undefined, groupName: string): Group | null => {
  if (!groups) return null;

  for (const group of groups) {
    if (group.assetGroupName === groupName) {
      return group;
    }
    const foundInChildren = findGroupInTree(group.childGroups, groupName);
    if (foundInChildren) {
      return foundInChildren;
    }
  }
  return null;
};

/**
 * Generates a new unique ID for a group based on existing group hash map
 */
export const generateNewGroupId = (groupHashMap: Record<number, Group>): number => {
  const existingIds = Object.keys(groupHashMap).map(Number);
  return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
};

/**
 * Creates a new group object with the provided parameters
 */
export const createNewGroup = (id: number, groupName: string, childWells?: Asset[]): Group => {
  return {
    id,
    assetGroupName: groupName,
    childGroups: [],
    assets: childWells || [],
    showAssets: false,
    class: 'navigation-content-frame-item',
  };
};

/**
 * Main function to handle custom view group updates
 * This handles both creating new groups and updating existing ones
 */
export const updateCustomViewGroupTree = (
  currentGroups: Group[] | null | undefined,
  groupHashMap: Record<number, Group>,
  selectedGroup: Group | undefined,
  params: UpdateGroupParams,
): GroupTreeOperationResult => {
  const { originalGroupName, newGroupName, newParentGroupName, childWells, isNewGroup } = params;

  let updatedGroups: Group[] | null = currentGroups || null;
  let updatedSelectedGroup: Group | null | undefined = selectedGroup;

  // If this is a new group, create it directly
  if (isNewGroup) {
    const newGroupId = generateNewGroupId(groupHashMap);
    const newGroup = createNewGroup(newGroupId, newGroupName, childWells);

    // Add to parent or root
    if (newParentGroupName === 'root' || newParentGroupName === 'Well Groups') {
      // Find the 'Well Groups' root group and add as its child
      const wellGroupsParent = currentGroups?.find((group) => group.assetGroupName === 'Well Groups');
      if (wellGroupsParent) {
        // Add as child of 'Well Groups'
        updatedGroups =
          currentGroups?.map((group) => {
            if (group.assetGroupName === 'Well Groups') {
              return {
                ...group,
                childGroups: [...group.childGroups, newGroup],
              };
            }
            return group;
          }) || null;
      } else {
        // Fallback: Add to root level if 'Well Groups' not found
        updatedGroups = currentGroups ? [...currentGroups, newGroup] : [newGroup];
      }
    } else {
      // Add to specified parent
      updatedGroups = addGroupToParent(currentGroups, newGroup, newParentGroupName);
    }
  } else {
    // Handle updating existing group
    const existingGroup = findGroupInTree(currentGroups, originalGroupName);

    if (existingGroup) {
      // Create the updated group object
      const groupToMove: Group = {
        ...existingGroup,
        assetGroupName: newGroupName,
        assets: childWells || existingGroup.assets,
      };

      // Check if parent has changed
      const currentParent = getCurrentParentName(currentGroups, originalGroupName);
      const needsToMove = newParentGroupName !== currentParent;

      if (needsToMove) {
        // Remove from old location
        const groupsAfterRemoval = removeGroupFromTree(currentGroups, originalGroupName);

        // Add to new location
        if (newParentGroupName === 'root' || newParentGroupName === 'Well Groups') {
          // Find the 'Well Groups' root group and add as its child
          const wellGroupsParent = groupsAfterRemoval?.find((group) => group.assetGroupName === 'Well Groups');
          if (wellGroupsParent) {
            // Add as child of 'Well Groups'
            updatedGroups =
              groupsAfterRemoval?.map((group) => {
                if (group.assetGroupName === 'Well Groups') {
                  return {
                    ...group,
                    childGroups: [...group.childGroups, groupToMove],
                  };
                }
                return group;
              }) || null;
          } else {
            // Fallback: Add to root level if 'Well Groups' not found
            updatedGroups = groupsAfterRemoval ? [...groupsAfterRemoval, groupToMove] : [groupToMove];
          }
        } else {
          // Add to specified parent
          updatedGroups = addGroupToParent(groupsAfterRemoval, groupToMove, newParentGroupName);
        }
      } else {
        // Just update in place (name or wells changed but not parent)
        updatedGroups = updateGroupInTree(currentGroups, originalGroupName, newGroupName, childWells);
      }

      // Update selected group reference if it was the one being modified
      if (selectedGroup && selectedGroup.assetGroupName === originalGroupName) {
        updatedSelectedGroup = {
          ...groupToMove,
        };
      }
    }
  }

  return {
    updatedGroups,
    updatedSelectedGroup,
  };
};
